import { LitElement, html, css } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import formBackgroundUrl from 'data-url:../static/images/form_background_contour_pattern.svg';
import { modernNormalizeStyleSheet, globalStyleSheet, utilsStyleSheet } from './shared-styles.js';

// macOS provide filenames in decomposed Unicode (NFD), which can render
// Turkish, Spanish, Korean combining marks with visual offsets in some fonts. Normalize to NFC.
const normalizeFileName = (name) => (typeof name === 'string' ? name.normalize('NFC') : '');

export default class FormUploadElement extends LitElement {
    static styles = [modernNormalizeStyleSheet, globalStyleSheet, utilsStyleSheet, css`
        :host {
            display: block;
        }

        /* Without this, setting the \`hidden\` attribute/property has no visible effect:
           the UA stylesheet's [hidden] { display: none; } is a lower-priority origin
           than this component's own :host { display: block; } rule above, so origin
           order (author beats user-agent, regardless of specificity) lets :host win
           and the element stays visible even while hidden. */
        :host([hidden]) {
            display: none;
        }

        .upload-form {
            animation: fadeIn 0.5s ease-out;
            background-color: var(--color-form-bg);
            /* background-image is set via styleMap() in render(), not here */
            border-radius: 20px;
            box-shadow: var(--shadow-input);
            padding-block: 30px;
        }

        .upload-form__file-select {
            align-items: center;
            background-color: var(--color-input-bg);
            border-radius: 10px;
            box-shadow: var(--shadow-input);
            display: flex;
            justify-content: space-between;
            overflow: hidden;
            padding-inline: 0.5rem;
        }

        .upload-form__text-input {
            color: var(--color-input-font-primary);
            cursor: pointer;
            flex-grow: 1;
            overflow: hidden;
            padding: 1rem;
            text-overflow: ellipsis;
            text-wrap: nowrap;
        }

        .upload-form__add-button {
            align-items: center;
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            font-size: 1.5rem;
            height: 35px;
            justify-content: center;
            width: 35px;
        }

        .upload-form__add-button:hover {
            color: var(--color-input-primary);
            fill: currentColor;
        }

        .upload-form__accept-terms {
            align-items: center;
            display: flex;
            font-family: var(--font-body);
            justify-content: center;
        }

        .upload-form__accept-terms--not-accepted {
            animation: shake-horizontal 0.8s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
        }

        .upload-form__accept-terms input[type="checkbox"] {
            margin-right: 0.5rem;
        }

        .upload-form__accept-terms label {
            cursor: pointer;
            user-select: none;
        }

        .upload-form__submit {
            display: block;
            width: 10em;
        }

        /* md */
        @media screen and (min-width: 448px) {
            .upload-form__text-input, .upload-form__accept-terms {
                font-size: 1rem;
            }
        }
        /* xl */
        @media screen and (min-width: 576px) {
            .upload-form__text-input, .upload-form__accept-terms {
                font-size: 1.25rem;
            }
        }

        /* 3xl */
        @media screen and (min-width: 768px) {
            .upload-form__text-input, .upload-form__accept-terms {
                font-size: 1.5rem;
            }
        }

        /* 5xl */
        @media screen and (min-width: 1024px) {
            .upload-form__file-select {
                max-width: 768px;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .upload-form {
                animation: none;
            }

            .upload-form__accept-terms--not-accepted {
                animation: reduced-motion-animation 2s linear;
            }
        }

        @keyframes shake-horizontal {
            0%,
            100% {
                transform: translateX(0);
            }
            10%,
            30%,
            50%,
            70% {
                transform: translateX(-10px);
            }
            20%,
            40%,
            60% {
                transform: translateX(10px);
            }
            80% {
                transform: translateX(8px);
            }
            90% {
                transform: translateX(-8px);
            }
        }

        @keyframes reduced-motion-animation {
            0% {
                opacity: 0;
                text-decoration: overline;
                text-decoration-thickness: 5px;
            }

            50% {
                text-decoration-thickness: 1px;
            }

            100% {
                opacity: 1;
                text-decoration: none;
            }
        }
    `];

    #selectedFile = null;

    // Bound as a class field (not a #-prefixed prototype method) so the same
    // reference can be passed to both addEventListener and removeEventListener —
    // same convention as ProgressDivElement's #handleReducedMotionChange.
    // Ported from static/script/index.js's `window.addEventListener('pageshow', ...)`:
    // browsers may restore form controls from bfcache on back/forward navigation,
    // so this form is reset on every pageshow, not just on first connect.
    #handlePageShow = () => {
        this.reset();
    };

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('pageshow', this.#handlePageShow);
    }

    disconnectedCallback() {
        window.removeEventListener('pageshow', this.#handlePageShow);
        super.disconnectedCallback();
    }

    // Read-only from outside: no setter is defined, so `formUploadEl.selectedFile = x`
    // throws (class bodies are strict mode) instead of silently doing nothing — the
    // only writer of #selectedFile is #handleFileChange, below.
    get selectedFile() {
        return this.#selectedFile;
    }

    // Public: called by an ancestor (e.g. <upload-container> on "Upload Another")
    // to clear the current selection/acceptance state before re-showing this form.
    // The native file input isn't reactive, so its value must be cleared here too —
    // otherwise the browser won't fire another change event for the same file path.
    reset() {
        this.#selectedFile = null;
        this.renderRoot.getElementById('hidden-file-input').value = '';
        this.renderRoot.getElementById('selected-file-name').value = '';
        this.renderRoot.getElementById('checkbox-accept').checked = false;
        // Strips a leftover shake-trigger class from an earlier rejected click in this
        // session. Without this, un-hiding the form (display: none → block) restarts
        // shake-horizontal from scratch if the class is still present, even though no
        // invalid click just happened — see _plans/4.2-lit_form_upload_component.md Step 16.
        this.renderRoot.querySelector('.upload-form__accept-terms')
            ?.classList.remove('upload-form__accept-terms--not-accepted');
    }

    // Public: called by an ancestor (e.g. <upload-container> on "Upload Another")
    // to move focus onto the file-select control once this form is shown again —
    // mirrors static/script/index.js's uploadAnotherButton handler calling
    // selectedFileName.focus() right after re-showing #form-upload.
    focusFileSelect() {
        this.renderRoot.getElementById('selected-file-name').focus();
    }

    // Opens the native file picker — bound to both the "+" button and the readonly
    // text input, mirroring static/script/index.js's fileSelectButton/selectedFileName handlers.
    #openFilePicker() {
        this.renderRoot.getElementById('hidden-file-input').click();
    }

    #handleFileChange(e) {
        const selectedFile = e.target.files && e.target.files[0];
        this.#selectedFile = selectedFile ?? null;
        this.renderRoot.getElementById('selected-file-name').value = selectedFile ? normalizeFileName(selectedFile.name) : '';
    }

    // Native :focus-visible can't tell Tab-focus apart from click/programmatic focus
    // for text inputs (browsers intentionally match it on click for those too). Mark
    // the input Tab lands on so CSS can show the outline only for that case.
    // Tab's keydown fires on the element about to lose focus; by keyup, focus has
    // already moved, so e.target here is the newly-focused element.
    #handleTextInputTabKeyUp(e) {
        if (e.key === 'Tab') {
            e.target.classList.add('tab-pressed');
        }
    }

    // Using blur (not focusout) clears the marker correctly when focus round-trips
    // through a native OS dialog (e.g. the file picker) and returns to the same input.
    #handleTextInputBlur(e) {
        e.target.classList.remove('tab-pressed');
    }

    // input[type="text"] and input[type="checkbox"] sit inside a <form> with no
    // type="submit" button. Per the HTML implicit-submission rule, a form with no submit
    // button still submits on Enter if it has exactly one single-line text field — which
    // is the shape of this form — reloading the page. Space has its own default
    // (checkbox toggle / page scroll). Prevent both defaults and instead fire the same
    // `click` each element already responds to, so keyboard activation matches mouse
    // activation exactly. `e.repeat` guards against auto-repeat firing click on every
    // repeated keydown while the key is held, matching native single-activation-per-press
    // button/checkbox behavior.
    #handleKeyboardActivate(e) {
        if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
            e.preventDefault();
            e.target.click();
        }
    }

    // Mirrors static/script/index.js's submitButton handler: clicking Upload without
    // the terms checkbox checked replays the shake-horizontal animation instead of submitting.
    // When accepted, this component doesn't hide itself — it announces the click and lets
    // an ancestor (e.g. <upload-container>) own cross-component visibility/coordination.
    #handleSubmitClick() {
        const acceptTermsCheckbox = this.renderRoot.getElementById('checkbox-accept');
        if (acceptTermsCheckbox && acceptTermsCheckbox.checked) {
            this.dispatchEvent(new CustomEvent('upload-button-clicked', { bubbles: true, composed: true }));
            return;
        }

        const acceptTermsContainer = this.renderRoot.querySelector('.upload-form__accept-terms');
        if (acceptTermsContainer) {
            // Re-adding the class retriggers the CSS animation on every invalid click.
            acceptTermsContainer.classList.remove('upload-form__accept-terms--not-accepted');
            // Force the browser to apply class removal now, so adding it again restarts the animation.
            // `void <expression>` runs the expression and discards its result (returns undefined).
            // Here we only need the side effect of reading layout (`offsetWidth`) to trigger reflow.
            void acceptTermsContainer.offsetWidth;
            acceptTermsContainer.classList.add('upload-form__accept-terms--not-accepted');
        }
    }

    render() {
        // background-image for .upload-form, kept out of static styles css since Parcel can't resolve data-url: inside it

        // .container-padding applied here (inside the shadow template), not on the host in index.html —
        // padding is this component's own internal layout decision, not the parent's to set. See utils.css.
        return html`
            <form id="form-upload" class="container-padding upload-form" autocomplete="off" style=${styleMap({ backgroundImage: `url(${formBackgroundUrl})` })}>
                <div class="upload-form__file-select icon--hoverable mi-auto">
                    <input id="hidden-file-input" type="file" hidden @change=${this.#handleFileChange}>
                    <label class="visually-hidden" for="selected-file-name">Select the file (Max. 5GB): </label>
                    <input id="selected-file-name" type="text" class="upload-form__text-input" placeholder="(Max 5GB) Select the file" readonly @click=${this.#openFilePicker} @keyup=${this.#handleTextInputTabKeyUp} @blur=${this.#handleTextInputBlur} @keydown=${this.#handleKeyboardActivate}/>
                    <i id="button-add" class="upload-form__add-button icon icon--plus-square" aria-hidden="true" @click=${this.#openFilePicker}></i>
                </div>
                <div class="upload-form__accept-terms mt-1rem">
                    <input id="checkbox-accept" type="checkbox" @keydown=${this.#handleKeyboardActivate}/>
                    <label for="checkbox-accept">I accept the terms & conditions</label>
                </div>
                <button class="upload-form__submit mt-1rem mi-auto" type="button" @click=${this.#handleSubmitClick}>
                    Upload
                    <i class="icon icon--paper-plane" aria-hidden="true"></i>
                </button>
            </form>
        `;
    }
}

customElements.define('tempup-form-upload', FormUploadElement);
